import { cn } from "@homeinstead/ui";
import { useMeasure } from "@uidotdev/usehooks";
import { AnimatePresence, motion } from "motion/react";
import * as React from "react";
import { useState } from "react";

export type InputProps = React.InputHTMLAttributes<HTMLInputElement>;

const labelVariants = {
  touched: {
    fontSize: "12px",
    opacity: "100%",
    top: "-8px",
  },
  untouched: {
    backgroundColor: "rgba(0,0,0,0)",
    fontSize: "14px",
    opacity: "0%",
    top: "16px",
  },
};

const backgroundVariants = {
  touched: ({ width, height }: { width: number; height: number }) => ({
    backgroundColor: "#fff",
    height: `${height}px`,
    top: "-8px",
    width: `${width}px`,
  }),
  untouched: {
    height: "14px",
    top: "-8px",
    width: "0%",
  },
};

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, type, onChange, ...props }, ref) => {
    const [isTouched, setIsTouched] = useState<boolean>();
    const [labelRef, bounds] = useMeasure();
    const width = bounds?.width || 0;
    const height = bounds?.height || 0;

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (onChange) onChange(e);
      setIsTouched(e?.target?.value !== "");
    };

    return (
      <motion.div
        className="group relative"
        transition={{ staggerChildren: 1 }}
      >
        <input
          className={cn(
            `z-50 flex h-12 w-full rounded-sm border border-new-beginnings-base bg-white px-3 py-1 text-base shadow-sm transition-colors file:border-0 file:bg-transparent file:font-medium file:text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-new-beginnings-base disabled:cursor-not-allowed disabled:opacity-50`,
            className,
          )}
          onChange={(e) => handleChange(e)}
          ref={ref}
          type={type}
          {...props}
        />
        <motion.div
          animate={isTouched ? "touched" : "untouched"}
          className={cn("absolute left-[9px] z-[60] min-w-fit text-xs")}
          custom={{ height, width }}
          initial="untouched"
          variants={backgroundVariants}
        />
        <AnimatePresence>
          {isTouched && (
            <motion.label
              animate={isTouched ? "touched" : "untouched"}
              className={cn(
                "absolute left-[9px] z-[60] px-1 text-new-beginnings-base text-xs",
              )}
              exit="untouched"
              initial="untouched"
              ref={labelRef}
              variants={labelVariants}
            >
              {props?.placeholder}
            </motion.label>
          )}
        </AnimatePresence>
      </motion.div>
    );
  },
);
Input.displayName = "Input";

export { Input };
